import React, { useEffect, useState } from 'react';
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { Link, useParams } from 'react-router-dom';
import { PrimaryButton } from "../UI";
import SuccessAlert from '../UI/Alert/SuccessAlert';
import { useUserContext } from '../../contexts/UserContext';
import EditIcon from '../../assets/icons/edit.svg'
import { addExperienceIdToHTML, findPathAndUpdateValue, isValidJsonString, updateHtmlTemplate, getObjFromHtml } from '../../utils/helper';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { Copy, Save, X } from 'lucide-react';

const ExperienceCode = ({ 
    experienceJSON,
    permissionId,
    setFieldValues,
    validationError,
    setValidationError,
    selectedTemplate,
    fieldValues,
    dynamicFields,
    setDynamicFields,
    code,
    setCode,
    experience   //passed to identify versions
}) => {
    const { id } = useParams();
    const [copySuccess, setCopySuccess] = useState(false);
    const [enableEdit, setEnableEdit] = useState(false);
    const [tempCode, setTempCode] = useState(code); // Temporary code for editing

    const { checkPermission } = useUserContext();
    const { enums, content } = useContentfulContext();

    useEffect(() => {
        if (!selectedTemplate) return;
        if ((selectedTemplate.type === enums?.templateTypes?.json_a_to_b?.id) || (selectedTemplate.type === enums?.templateTypes?.no_preview?.id)) {
            try {
                if (!selectedTemplate.json_schema) return;
                const parsedJson = typeof selectedTemplate.json_schema === 'string'
                    ? JSON.parse(selectedTemplate.json_schema)
                    : selectedTemplate.json_schema;
                let temp = JSON.parse(JSON.stringify(parsedJson));

                if (fieldValues && typeof fieldValues === 'object') {
                    Object.keys(fieldValues).forEach((key) => {
                        const val = findPathAndUpdateValue(parsedJson, key);

                        if (val !== undefined) {
                            const x = findPathAndUpdateValue(temp, key, fieldValues[key]);
                            if (x?.updatedObj) temp = x.updatedObj;
                        } else if (key.includes('.')) {
                            const parentPath = key.slice(0, key.lastIndexOf('.'));
                            const x = findPathAndUpdateValue(temp, parentPath);
                            if (typeof x === 'string') {
                                const placeholder = "${template." + key + "}";
                                const replacement = fieldValues[key] !== undefined && fieldValues[key] !== null ? String(fieldValues[key]) : "";
                                const updatedVal = x.replaceAll(placeholder, replacement);
                                const updated = findPathAndUpdateValue(temp, parentPath, updatedVal);
                                if (updated?.updatedObj) temp = updated.updatedObj;
                            } else {
                                const directUpdate = findPathAndUpdateValue(temp, key, fieldValues[key]);
                                if (directUpdate?.updatedObj) temp = directUpdate.updatedObj;
                            }
                        } else {
                            const directUpdate = findPathAndUpdateValue(temp, key, fieldValues[key]);
                            if (directUpdate?.updatedObj) temp = directUpdate.updatedObj;
                        }
                    });
                }

                let newCode = JSON.stringify(temp, null, 2);
                if (fieldValues && typeof fieldValues === 'object') {
                    Object.keys(fieldValues).forEach((key) => {
                        const placeholder = "${template." + key + "}";
                        if (newCode.includes(placeholder)) {
                            const replacement = fieldValues[key] !== undefined && fieldValues[key] !== null ? String(fieldValues[key]) : "";
                            newCode = newCode.replaceAll(placeholder, replacement);
                        }
                    });
                }
                newCode = newCode.replaceAll('&lt;', '<')?.replaceAll('&gt;', '>');
                setCode(newCode);
                setTempCode(newCode);
            } catch (err) {
                console.error("Error processing template JSON schema:", err);
            }
        } else if (selectedTemplate.type === enums?.templateTypes?.json_a_to_html?.id) {
            const updatedHTML = updateHtmlTemplate(selectedTemplate.html, fieldValues);
            const versionNo = experience?.versions[experience?.versions?.length - 1]?.version_no || 1;
            const newCode = addExperienceIdToHTML(updatedHTML, experience?._id, versionNo);
            setCode(newCode);
            setTempCode(newCode);
        } else {
            if (dynamicFields && Object.keys(dynamicFields).length > 0) {
                let temp = { ...experienceJSON };
                Object.keys(dynamicFields).forEach((key) => {
                    const res = findPathAndUpdateValue(temp, key, dynamicFields[key]);
                    if (res?.updatedObj) temp = { ...res.updatedObj };
                });
                const newCode = JSON.stringify(temp, null, 2);
                setCode(newCode);
                setTempCode(newCode);
            } else {
                const newCode = JSON.stringify(experienceJSON || {}, null, 2);
                setCode(newCode);
                setTempCode(newCode);
            }
        }
    }, [experienceJSON, fieldValues, selectedTemplate, dynamicFields]);

    // Sync tempCode when code changes from external source
    useEffect(() => {
        if (!enableEdit) {
            setTempCode(code);
        }
    }, [code, enableEdit]);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopySuccess(true);
        }).catch(err => {
            console.error('Error copying text: ', err);
        });
    }

    function getFlattenExperienceJSON(ob) {
        let result = {};

        function recurse(cur, prop) {
            if (Object(cur) !== cur) {
                result[prop] = cur;

                if(dynamicFields[prop] !== undefined) {
                    result[prop] = fieldValues[prop];
                    setDynamicFields((prev) => ({
                        ...prev,
                        [prop]: cur
                    }))
                } else {
                    result[prop] = cur;
                }
            } else if (Array.isArray(cur)) {
                for (let i = 0; i < cur.length; i++) {
                    recurse(cur[i], prop ? prop + "[" + i + "]" : i);
                }
                if (cur.length === 0) result[prop] = [];
            } else {
                let isEmpty = true;
                for (let p in cur) {
                    isEmpty = false;
                    recurse(cur[p], prop ? prop + "." + p : p);
                }
                if (isEmpty && prop) result[prop] = {};
            }
        }
        recurse(ob, "");

        for (const key in result) {
            if (result.hasOwnProperty(key)) {
                if(key.includes('.0.')) {
                    result[key.replace('.0.', '[0].')] = result[key]
                    delete result[key];
                } else if(key.includes('.1.')) {
                    result[key.replace('.1.', '[1].')] = result[key]
                    delete result[key];
                } else if(key.includes('.2.')) {
                    result[key.replace('.2.', '[2].')] = result[key]
                    delete result[key];
                }
            }
        }
        return result;
    }

    const handleOnChange = (e) => {
        // Just update temporary code, don't validate or update fieldValues yet
        setTempCode(e);
    }

    const handleSave = () => {
        // if(selectedTemplate.type === enums?.templateTypes.json_a_to_b.id) {
        //     setEnableEdit(false);
        //     return;
        // }

        if(selectedTemplate.type === enums?.templateTypes.json_a_to_html.id){
            try {
                // For HTML templates, we need to extract JSON from HTML
                const parsedJsonSchema = JSON.parse(selectedTemplate.json_schema);
                const extractedJson = getObjFromHtml(tempCode, parsedJsonSchema.config_obj_key);
                
                // Update the main code state with HTML
                setCode(tempCode);
                
                // Extract and update field values from the extracted JSON
                const updatedFieldValues = getFlattenExperienceJSON(extractedJson);
                setFieldValues(updatedFieldValues);
                
                setEnableEdit(false);
                setValidationError("");
            } catch (error) {
                console.error("Error parsing HTML template:", error);
                setValidationError("Error parsing HTML template. Please check the format.");
                return;
            }
            return;
        } else {
            const isValidJson = isValidJsonString(tempCode);
            if(!isValidJson) {
                setValidationError(content.EDIT_EXPERIENCE.invalid_json);
                return;
            }
            setValidationError("");
            
            // Update the main code state
            setCode(tempCode);
            
            // Extract and update field values so they reflect in configure step
            const updatedFieldValues = getFlattenExperienceJSON(JSON.parse(tempCode));
            setFieldValues(updatedFieldValues);
            
            setEnableEdit(false);
        }
    }

    const handleCancel = () => {
        // Reset tempCode to the original code
        setTempCode(code);
        setValidationError("");
        setEnableEdit(false);
    }

    return (
        <div className="h-full flex flex-col min-h-[500px]">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-3 gap-3">
                <h1 className="text-xl font-medium tracking-tight text-gray-900 md:text-2xl">
                    {content.EDIT_EXPERIENCE.exp_code}
                </h1>

                <div className="flex items-center gap-2">
                    {/* Copy Icon Button */}
                    <button
                        onClick={() => copyToClipboard(code)}
                        className="p-2 rounded-md border border-gray-300 hover:bg-gray-100 transition"
                        title={content.EDIT_EXPERIENCE.copy}
                    >
                        <Copy className="w-4 h-4 text-gray-700" />
                    </button>

                    {checkPermission(permissionId) && (
                        !enableEdit ? (
                            <img
                                alt=""
                                src={EditIcon}
                                className="w-[20px] cursor-pointer"
                                onClick={() => setEnableEdit(true)}
                                title="Edit"
                            />
                        ) : (
                            <>
                                <button
                                    className="p-2 rounded-md bg-primary text-white hover:bg-primary-2 transition"
                                    onClick={handleSave}
                                    title="Save"
                                >
                                    <Save className="w-4 h-4" />
                                </button>

                                <button
                                    className="p-2 rounded-md bg-gray-300 text-gray-700 hover:bg-gray-400 transition"
                                    onClick={handleCancel}
                                    title="Cancel"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </>
                        )
                    )}
                </div>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
                <div className="h-full overflow-auto rounded border border-gray-200">
                    <CodeMirror
                        value={enableEdit ? tempCode : code}
                        height="100%"
                        readOnly={!enableEdit}
                        editable={enableEdit}
                        lineWrapping={true}
                        extensions={[EditorView.lineWrapping]}
                        onChange={handleOnChange}
                    />
                </div>
            </div>
            {validationError && <pre><div className="text-base leading-tight tracking-tight text-red-500 mt-3">{validationError}</div></pre>}
            { copySuccess && <SuccessAlert disappearTime="2000" onDisappear={() => {
                setCopySuccess(false);
            }}>{content.EDIT_EXPERIENCE.copy_success_msg}</SuccessAlert> }
        </div>
    )
}

export default ExperienceCode;